import { useCallback, useEffect, useRef, useState } from 'react';
import aa from 'search-insights';

import { getGoogleAnalyticsUserToken } from '@/lib/algoliaUserToken';
import { ALGOLIA_API_KEY, ALGOLIA_APP_ID } from '@/lib/const';
import { useAlgoliaInsightsEnabled } from '@/providers/ThirdPartyFlagsProvider/ThirdPartyFlagsContext';

/**
 * Initialize Algolia search-insights.
 *
 * NOTE: We only call aa('init') here, NOT aa('setUserToken').
 * The userToken is passed directly to InstantSearch via the `insights` prop's
 * `insightsInitParams.userToken`. Calling setUserToken separately would trigger
 * the insights middleware's onUserTokenChange callback, causing an extra search.
 *
 * This hook deliberately avoids useAlgoliaUserToken() to prevent subscribing
 * to userState in _app.tsx, which would cause cascade re-renders on every
 * auth state change and destabilize components like reCAPTCHA.
 */
export const useSearchInsights = () => {
  const isAlgoliaEnabled = useAlgoliaInsightsEnabled();
  const [hasGoogleAnalytics, setHasGoogleAnalytics] = useState(() => {
    if (typeof window === 'undefined') return false;
    return !!getGoogleAnalyticsUserToken();
  });
  const hasInitialized = useRef(false);

  const checkGA = useCallback(() => {
    const ga = !!getGoogleAnalyticsUserToken();
    setHasGoogleAnalytics((prev) => (prev !== ga ? ga : prev));
  }, []);

  useEffect(() => {
    const handleUpdate = () => setTimeout(checkGA, 100);

    if (typeof window !== 'undefined' && window.OneTrust) {
      window.OneTrust.OnConsentChanged(handleUpdate);
    }
    window.addEventListener('OneTrustGroupsUpdated', handleUpdate);

    return () => {
      window.removeEventListener('OneTrustGroupsUpdated', handleUpdate);
    };
  }, [checkGA]);

  useEffect(() => {
    if (!isAlgoliaEnabled) return;
    if (!hasInitialized.current || hasGoogleAnalytics) {
      aa('init', {
        appId: ALGOLIA_APP_ID,
        apiKey: ALGOLIA_API_KEY,
        useCookie: hasGoogleAnalytics,
        partial: true,
      });
      hasInitialized.current = true;
    }
  }, [hasGoogleAnalytics, isAlgoliaEnabled]);
};
